import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import type { AuditRepository } from './repository';
import type { AuditLogQuery, SecurityEventQuery } from './types';

type AuditLogListResponse = ApiResponse<'/api/v1/audit-logs', 'get'>;
type AuditLogResponse = ApiResponse<'/api/v1/audit-logs/{id}', 'get'>;
type SecurityEventListResponse = ApiResponse<'/api/v1/security-events', 'get'>;
type SecurityEventResponse = ApiResponse<'/api/v1/security-events/{id}', 'get'>;

export function createRealAuditRepository(
  client: ApiClient,
  accessToken: string | null,
): AuditRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async listAuditLogs(query, signal) {
      const response = await client.get<AuditLogListResponse>('/api/v1/audit-logs', {
        ...auth,
        query: auditLogQuery(query),
        signal,
      });
      return page(response);
    },
    async getAuditLog(auditId, signal) {
      const response = await client.get<AuditLogResponse>(auditLogPath(auditId), {
        ...auth,
        signal,
      });
      return response.data.data;
    },
    async listSecurityEvents(query, signal) {
      const response = await client.get<SecurityEventListResponse>('/api/v1/security-events', {
        ...auth,
        query: securityEventQuery(query),
        signal,
      });
      return page(response);
    },
    async getSecurityEvent(eventId, signal) {
      const response = await client.get<SecurityEventResponse>(securityEventPath(eventId), {
        ...auth,
        signal,
      });
      return response.data.data;
    },
  };
}

function auditLogQuery(query: AuditLogQuery) {
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    principalActorId: query.filters.principalActorId.trim(),
    principalRole: query.filters.principalRole,
    partnerId: query.filters.partnerId.trim(),
    agentId: query.filters.agentId.trim(),
    requestId: query.filters.requestId.trim(),
    sourceIp: query.filters.sourceIp.trim(),
    from: bangkokWallTimeToRfc3339(query.filters.from),
    to: bangkokWallTimeToRfc3339(query.filters.to),
    action: normalizeCode(query.filters.action),
    entityType: normalizeCode(query.filters.entityType),
    entityId: query.filters.entityId.trim(),
  });
}

function securityEventQuery(query: SecurityEventQuery) {
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    principalActorId: query.filters.principalActorId.trim(),
    principalRole: query.filters.principalRole,
    partnerId: query.filters.partnerId.trim(),
    agentId: query.filters.agentId.trim(),
    requestId: query.filters.requestId.trim(),
    sourceIp: query.filters.sourceIp.trim(),
    from: bangkokWallTimeToRfc3339(query.filters.from),
    to: bangkokWallTimeToRfc3339(query.filters.to),
    credentialActorId: query.filters.credentialActorId.trim(),
    category: normalizeCode(query.filters.category),
    severity: query.filters.severity,
    eventCode: normalizeCode(query.filters.eventCode),
  });
}

function normalizeCode(value: string): string {
  return value.trim().toUpperCase();
}

function page<Item>(response: {
  readonly data: {
    readonly data: readonly Item[];
    readonly pagination: { readonly nextCursor: string | null };
  };
  readonly requestId: string | null;
}) {
  return {
    items: response.data.data,
    nextCursor: response.data.pagination.nextCursor,
    requestId: response.requestId,
  };
}

function compact(values: Readonly<Record<string, unknown>>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

function auditLogPath(auditId: string): string {
  return `/api/v1/audit-logs/${encodeURIComponent(auditId)}`;
}

function securityEventPath(eventId: string): string {
  return `/api/v1/security-events/${encodeURIComponent(eventId)}`;
}
